import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"
import * as React from "react"
import { cn } from "../utils"
import { Button } from "./Button"

const SHEET_DEFAULT_ANIMATION_DURATION = 300

const SheetContext = React.createContext<{
  onOpenChangeProp: ((open: boolean) => void) | undefined
  onOpenChange: (open: boolean) => void
}>({
  onOpenChangeProp: (_: boolean) => {},
  onOpenChange: (_: boolean) => {},
})

function useSheetContext() {
  const context = React.useContext(SheetContext)
  if (!context) {
    throw new Error("useSheetContext must be used within a SheetProvider")
  }
  return context
}

const Sheet = ({
  open: openProp,
  onOpenChange: onOpenChangeProp,
  onSheetOpenChange,
  children,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Root> & {
  onSheetOpenChange?: (open: boolean) => void
}) => {
  const [open, setOpen] = React.useState(openProp)

  function onOpenChange(open: boolean) {
    setOpen(false)
    onSheetOpenChange?.(open)
    setTimeout(() => {
      onOpenChangeProp?.(open)
    }, SHEET_DEFAULT_ANIMATION_DURATION)
  }

  React.useEffect(() => {
    setOpen(openProp)
  }, [openProp])

  return (
    <SheetPrimitive.Root
      {...props}
      onOpenChange={onOpenChange}
      open={openProp === undefined ? undefined : open}
      modal={false}
    >
      <SheetContext.Provider
        value={{
          onOpenChangeProp,
          onOpenChange,
        }}
      >
        {children}
      </SheetContext.Provider>
    </SheetPrimitive.Root>
  )
}

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay> & {
    container?: HTMLElement | null
  }
>(({ className, container, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      container ? "absolute inset-0" : "fixed inset-0",
      "z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      "data-[state=closed]:pointer-events-none",
      className,
    )}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  "z-50 min-w-xs gap-4 bg-background p-6 shadow-lg transition ease-in-out overflow-y-auto duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      position: {
        fixed: "fixed",
        absolute: "absolute",
      },
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      position: "fixed",
      side: "right",
    },
  },
)

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    Omit<VariantProps<typeof sheetVariants>, "position"> {
  container?: HTMLElement | null
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", className, children, container, ...props }, ref) => {
  const position = container ? "absolute" : "fixed"
  const { onOpenChange } = useSheetContext()

  function onFocusOutside(
    event: NonNullable<
      Parameters<
        NonNullable<React.ComponentProps<typeof SheetPrimitive.Content>["onFocusOutside"]>
      >[0]
    >,
  ) {
    // Non-modal sheets should not auto-dismiss when focus moves outside.
    // Dismissal on outside clicks is handled by onPointerDownOutside instead.
    event.preventDefault()
  }

  function onPointerDownOutside(
    event: NonNullable<
      Parameters<
        NonNullable<React.ComponentProps<typeof SheetPrimitive.Content>["onPointerDownOutside"]>
      >[0]
    >,
  ) {
    if (!onOpenChange) return

    const target = event.target as HTMLElement

    // Check if the target or any of its ancestors are interactive elements
    const interactableElements = ["BUTTON", "A", "INPUT", "SELECT", "TEXTAREA"]

    let currentElement: HTMLElement | null = target
    let iterations = 0
    const MAX_ITERATIONS = 4
    while (currentElement && iterations < MAX_ITERATIONS) {
      if (interactableElements.includes(currentElement.tagName ?? "")) return
      if (currentElement.onclick) return

      // Stop traversing if we reach the document body or a sheet content element
      if (
        currentElement.tagName === "BODY" ||
        currentElement.getAttribute("data-radix-dialog-content") !== null
      ) {
        break
      }

      currentElement = currentElement.parentElement
      iterations++
    }

    event.stopPropagation()
    onOpenChange(false)
  }

  return (
    <SheetPortal container={container}>
      <SheetPrimitive.Content
        ref={ref}
        className={cn(sheetVariants({ position, side }), className)}
        onPointerDownOutside={onPointerDownOutside}
        onFocusOutside={onFocusOutside}
        {...props}
      >
        <SheetPrimitive.Close
          className={cn(
            "absolute right-2 top-2 w-7 h-7",
            "z-50 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary",
          )}
          asChild
        >
          <Button variant="ghost" size="icon">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        </SheetPrimitive.Close>
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
})
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-2 text-center sm:text-left", className)} {...props} />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props}
  />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
}
